import React, { useEffect, useState } from 'react';
import { Props } from './Current.types';
import fields, { FIELDS, INPUTS } from 'modules/cm/fields/types';
import { Box, Columns } from 'modules/af_cm_ui_kit/components';
import { CurrentPension } from 'modules/cm/pages/CaseDetailsPage/CaseDetailsPage.types';
import validationSchema from './validationSchema';
import { VARIANTS_TYPES } from 'modules/af_cm_ui_kit/components/TextField/variants.types';
import { useDispatch } from 'react-redux';
import UpdatableField from 'modules/cm/fields/UpdatableField';
import { FormProvider, useForm } from 'react-hook-form';
import useYupValidationResolver from '../../../../../../../../form/yup/reactHookFormResolver';
import { updateApplicant } from '../../../../../../store/cases/update/actions';
import useSaveButtonClicked from '../../../../hooks/useSaveButtonClicked';
import { saveCase } from '../../../../../../store/cases/actions';
import * as subPages from '../../../../../../store/subPages';
import { SUBPAGES } from '../../../../../../store/subPages/types';
import CheckDirty from '../../../../../../components/CheckDirty/CheckDirty';

const Current: React.FC<Props> = ({
  modulePrefix,
  clearErrors,
  getErrors,
  data,
  initialData,
  caseId,
  applicantId,
  applicantIndex,
}) => {
  const dispatch = useDispatch();
  const defaultValues = (data: any): CurrentPension => ({
    [FIELDS.CURRENT_STATE_PENSION]: data[FIELDS.CURRENT_STATE_PENSION],
    [FIELDS.CURRENT_DB_NAME_OF_SCHEME]: data[FIELDS.CURRENT_DB_NAME_OF_SCHEME],
    [FIELDS.CURRENT_DB_ANNUAL_INCOME]: data[FIELDS.CURRENT_DB_ANNUAL_INCOME],
    [FIELDS.CURRENT_DB_PERCENT_PASSED_ON_DEATH]: data[FIELDS.CURRENT_DB_PERCENT_PASSED_ON_DEATH],
    [FIELDS.CURRENT_DC_NAME_OF_SCHEME]: data[FIELDS.CURRENT_DC_NAME_OF_SCHEME],
    [FIELDS.CURRENT_DC_CURRENT_VALUE]: data[FIELDS.CURRENT_DC_CURRENT_VALUE],
    [FIELDS.CURRENT_DC_PERCENT_PASSED_ON_DEATH]: data[FIELDS.CURRENT_DC_PERCENT_PASSED_ON_DEATH],
    [FIELDS.CURRENT_DC_ANNUAL_INCOME_DRAWN]: data[FIELDS.CURRENT_DC_ANNUAL_INCOME_DRAWN],
    [FIELDS.CURRENT_ANNUITY_NAME_OF_ANNUITY_PROVIDER]: data[FIELDS.CURRENT_ANNUITY_NAME_OF_ANNUITY_PROVIDER],
    [FIELDS.CURRENT_ANNUITY_GROSS_ANNUAL_INCOME]: data[FIELDS.CURRENT_ANNUITY_GROSS_ANNUAL_INCOME],
    [FIELDS.CURRENT_ANNUITY_PERCENT_PASSED_ON_DEATH]: data[FIELDS.CURRENT_ANNUITY_PERCENT_PASSED_ON_DEATH],
  });
  const [currentApplicantIndex, setCurrentApplicantIndex] = useState(applicantIndex);

  const methods = useForm<CurrentPension>({
    mode: 'onBlur',
    defaultValues: defaultValues(initialData),
    resolver: useYupValidationResolver(validationSchema),
  });

  const { reset } = methods;

  useEffect(() => {
    reset(defaultValues(data), {
      keepDefaultValues: currentApplicantIndex === applicantIndex,
    });
    if (currentApplicantIndex !== applicantIndex) {
      setCurrentApplicantIndex(applicantIndex);
    }
  }, [data, applicantIndex, currentApplicantIndex, reset]);

  const { addListener, removeListener } = useSaveButtonClicked();
  useEffect(() => {
    const listener = addListener(() => {
      dispatch(updateApplicant(methods.getValues(), applicantIndex));
      dispatch(saveCase());
    });
    return () => {
      removeListener(listener);
      dispatch(updateApplicant(methods.getValues(), applicantIndex));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantIndex]);

  return (
    <>
      <FormProvider<CurrentPension> {...methods}>
        <CheckDirty
          initialData={methods.getValues()}
          onDirtyChange={(isDirty) => {
            dispatch(
              subPages.actions.updateApplicantSubPageIsDirty(
                applicantIndex,
                SUBPAGES.APPLICANT_PENSIONS_CURRENT,
                isDirty,
              ),
            );
          }}
        />
        <form onSubmit={() => {}}>
          <Columns spacing={4}>
            <>
              <Box title="State Pension Income">
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.CURRENT_STATE_PENSION]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
              </Box>
              <Box title="Defined Contribution">
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.CURRENT_DC_NAME_OF_SCHEME]}
                  variant={VARIANTS_TYPES.TEXT}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.CURRENT_DC_CURRENT_VALUE]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.CURRENT_DC_PERCENT_PASSED_ON_DEATH]}
                  variant={VARIANTS_TYPES.PERCENTAGE}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.CURRENT_DC_ANNUAL_INCOME_DRAWN]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
              </Box>
            </>
            <>
              <Box title="Defined Benefit">
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.CURRENT_DB_NAME_OF_SCHEME]}
                  variant={VARIANTS_TYPES.TEXT}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.CURRENT_DB_ANNUAL_INCOME]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.CURRENT_DB_PERCENT_PASSED_ON_DEATH]}
                  variant={VARIANTS_TYPES.PERCENTAGE}
                />
              </Box>
              <Box title="Annuity">
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.CURRENT_ANNUITY_NAME_OF_ANNUITY_PROVIDER]}
                  variant={VARIANTS_TYPES.TEXT}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.CURRENT_ANNUITY_GROSS_ANNUAL_INCOME]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.CURRENT_ANNUITY_PERCENT_PASSED_ON_DEATH]}
                  variant={VARIANTS_TYPES.PERCENTAGE}
                />
              </Box>
            </>
          </Columns>
        </form>
      </FormProvider>
    </>
  );
};

export default Current;
